import { AxiosRequestConfig } from "axios";
import { useEffect, useRef, useState } from "react";
import useGet from "./useGet";
import useCache from "./cache/useCache";
import { extractApiPath, setToDB } from "src/core/api/cache/idbUtility";
import { getChangedSince, clearChangedSince, setChangedSince } from "src/core/api/cache/changedSinceStore";

export const useGetAllPaginatedItems = <ItemType,>({ requestFunction, pageSize = 500, idField = "ProductId", requestValues = null, shouldRequest = true, incremental = null, refreshKey, }: UseGetAllPaginatedItemsConfig<ItemType>): UseGetAllPaginatedItemsResult<ItemType> => {
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<ItemType[] | null>(null);

  // Only incremented when we truly want to kick off a request (start/resume)
  const fetchTokenRef = useRef(0);
  const [fetchToken, setFetchToken] = useState(0);

  const rowsRef = useRef<ItemType[]>([]);
  const freshRowsRef = useRef<ItemType[] | null>(null);
  const rowIndexRef = useRef<Map<any, number>>(new Map());
  const pageRef = useRef(1);
  const hasInitializedRef = useRef(false);
  const phaseRef = useRef<PaginationPhase>("DONE");
  const lastRefreshKeyRef = useRef(refreshKey);
  const pendingRefreshRef = useRef(false);

  const cacheLengthRef = useRef(0);
  const lastExposeTimeRef = useRef<number | null>(null);
  const cacheDirtyRef = useRef(false);

  const pausedRef = useRef(false);
  const pendingStepRef = useRef<PendingStep>(null);

  // Incremental support
  const incrementalEnabled = !!incremental?.enabled;
  const requireBaseCache = incremental?.requireBaseCache !== false;

  const runStartedAtRef = useRef<string | null>(null);
  const effectiveChangedSinceRef = useRef<string | null>(null);

  const buildRequestValues =
    incremental?.buildRequestValues ||
    ((base: any, changedSince: string | null) => {
      if (!changedSince) return base;
      const b = base && typeof base === "object" ? base : {};
      return { ...b, changedSince };
    });

  const computeNextMarker = incremental?.computeNextMarker || ((ctx: { runStartedAt: string }) => ctx.runStartedAt);

  // Normalize shouldRequest into a ref so scheduling can read it synchronously.
  const boolShouldRequest = isBoolRef(shouldRequest) ? shouldRequest.current : !!shouldRequest;
  const shouldRequestRef = useRef<boolean>(boolShouldRequest);
  useEffect(() => { shouldRequestRef.current = isBoolRef(shouldRequest) ? shouldRequest.current : !!shouldRequest; }, [shouldRequest, isBoolRef(shouldRequest) ? shouldRequest.current : shouldRequest]);

  const originalRequestUrl = requestFunction(pageSize, 0, requestValues).url;
  const cacheKey = extractApiPath(originalRequestUrl);

  const incrementalKey = incremental?.key || cacheKey;
  const storedChangedSince = incrementalEnabled ? getChangedSince(incrementalKey) : null;

  const debugEnabled = ENABLE_DEBUG;
  const { getRunId, log, warn } = usePaginatedDebugLogger(debugEnabled);

  const [cachedValue, setCachedValue, isCacheLoaded] = useCache<ItemType[]>(originalRequestUrl);

  const [metaCacheSize, setMetaCacheSize] = useState<number | null>(null);

  useEffect(() => {
    const size = readCacheSizeMeta(cacheKey);
    setMetaCacheSize(size);
  }, [cacheKey]);

  const previewPageSize = metaCacheSize === 0 ? Math.max(1, Math.floor(pageSize * 0.05)) : pageSize;

  const getByPath = (obj: any, path: IdPath) => {
    if (!obj) return undefined;
    if (typeof path === "string") return obj?.[path];
    return path.reduce((acc: any, key: string) => (acc == null ? acc : acc[key]), obj);
  };

  const getId = (obj: any) => getByPath(obj, idField);

  const rebuildRowIndex = (rows: ItemType[]) => {
    const map = new Map<any, number>();
    rows.forEach((row, idx) => {
      const id = getId(row);
      if (id != null) map.set(id, idx);
    });
    rowIndexRef.current = map;
  };

  const mergeIncomingIntoDisplayedRows = (incoming: ItemType[], options?: { prependNew?: boolean }) => {
    let changed = false;
    const newRowsToPrepend: ItemType[] = [];
    for (const newItem of incoming) {
      const id = getId(newItem);
      if (id == null) continue;
      const existingIdx = rowIndexRef.current.get(id);
      if (existingIdx === undefined) {
        if (options?.prependNew) {
          newRowsToPrepend.push(newItem);
        } else {
          rowIndexRef.current.set(id, rowsRef.current.length);
          rowsRef.current.push(newItem);
        }
        changed = true;
        continue;
      }
      const oldItem = rowsRef.current[existingIdx];
      if (oldItem !== newItem) {
        rowsRef.current[existingIdx] = newItem;
        changed = true;
      }
    }
    if (newRowsToPrepend.length > 0) {
      rowsRef.current = [...newRowsToPrepend, ...rowsRef.current];
      rebuildRowIndex(rowsRef.current);
    }
    return changed;
  };

  const persistSnapshot = (snapshot: ItemType[]) => {
    setCachedValue(snapshot);
    cacheLengthRef.current = snapshot.length;
    writeCacheSizeMeta(cacheKey, snapshot.length);
    cacheDirtyRef.current = false;
    rebuildRowIndex(snapshot);
  };

  useEffect(() => {
    if (hasInitializedRef.current) return;

    const runId = getRunId();
    const hasCacheData = Array.isArray(cachedValue) && cachedValue.length > 0;

    // If cache data shows up, use it immediately even if it arrived slightly later.
    if (hasCacheData) {
      log(
        `[useGetAllPaginatedItemsV2][${runId}] init from cached rows`,
        { cacheKey, metaCacheSize, cachedValueLen: cachedValue.length, incrementalEnabled, requireBaseCache, storedChangedSince, }
      );

      const snapshot = [...cachedValue];
      rowsRef.current = snapshot;
      freshRowsRef.current = null;
      rebuildRowIndex(snapshot);
      setItems(snapshot);
      cacheLengthRef.current = snapshot.length;
      cacheDirtyRef.current = false;

      if (incrementalEnabled) {
        effectiveChangedSinceRef.current = requireBaseCache ? storedChangedSince : storedChangedSince;
      } else {
        effectiveChangedSinceRef.current = null;
      }

      log(`[useGetAllPaginatedItemsV2][${runId}] hydrated rows from cache`, { len: snapshot.length, effectiveChangedSince: effectiveChangedSinceRef.current, });

      hasInitializedRef.current = true;
      return;
    }

    // Do not initialize the "empty cache" path until cache loading is actually finished.
    if (!isCacheLoaded) return;

    log(
      `[useGetAllPaginatedItemsV2][${runId}] init with no cached rows`,
      { cacheKey, metaCacheSize, cachedValueLen: Array.isArray(cachedValue) ? cachedValue.length : 0, incrementalEnabled, requireBaseCache, storedChangedSince, }
    );

    rowsRef.current = [];
    freshRowsRef.current = null;
    rebuildRowIndex([]);
    setItems(null);
    cacheLengthRef.current = 0;
    cacheDirtyRef.current = false;

    if (incrementalEnabled && requireBaseCache) {
      warn(
        `[useGetAllPaginatedItemsV2][${runId}] clearing changedSince because base cache is empty`,
        { incrementalKey }
      );
      clearChangedSince(incrementalKey);
    }

    if (incrementalEnabled) {
      effectiveChangedSinceRef.current = requireBaseCache ? null : storedChangedSince;
    } else {
      effectiveChangedSinceRef.current = null;
    }

    log(`[useGetAllPaginatedItemsV2][${runId}] effectiveChangedSince resolved`, { effectiveChangedSince: effectiveChangedSinceRef.current, });

    hasInitializedRef.current = true;
  }, [isCacheLoaded, cachedValue, cacheKey, metaCacheSize, incrementalEnabled, requireBaseCache, storedChangedSince, incrementalKey,]);

  const getEffectiveRequestValues = () => {
    if (!incrementalEnabled) return requestValues;
    return buildRequestValues(requestValues, effectiveChangedSinceRef.current);
  };

  const [request, setRequest] = useState<AxiosRequestConfig | null>(null);

  useEffect(() => {
    if (Object.is(lastRefreshKeyRef.current, refreshKey)) return;
    lastRefreshKeyRef.current = refreshKey;
    if (phaseRef.current !== "DONE") pendingRefreshRef.current = true;
  }, [refreshKey]);

  // Gate useGet so it does NOT fire while request is null.
  // Also: when paused, keep dependency stable so no new fetch starts.
  const depForUseGet = shouldRequestRef.current && request && !pausedRef.current ? `${fetchToken}:${page}` : "IDLE";

  const { results, status } = useGet(request as any, { dependency: depForUseGet });

  // Toggle pause (no aborting)
  useEffect(() => {
    const runId = getRunId();
    pausedRef.current = !shouldRequestRef.current;
    log(`[useGetAllPaginatedItemsV2][${runId}] pause toggled`, { shouldRequest: shouldRequestRef.current, paused: pausedRef.current, });
    if (!shouldRequestRef.current) pendingStepRef.current = null;
  }, [shouldRequest, isBoolRef(shouldRequest) ? shouldRequest.current : shouldRequest]);

  const kickFetch = () => {
    fetchTokenRef.current += 1;
    setFetchToken(fetchTokenRef.current);
  };

  const restartIfRefreshPending = () => {
    if (!pendingRefreshRef.current) return;
    pendingRefreshRef.current = false;
    setRequest(null);
  };

  // Start or resume
  useEffect(() => {
    if (!shouldRequestRef.current) return;
    if (!isCacheLoaded) return;
    if (!hasInitializedRef.current) return;
    if (metaCacheSize === null) return;

    const runId = getRunId();

    // 1) If we have a pending step (paused after receiving a page), resume from it
    if (pendingStepRef.current) {
      const pending = pendingStepRef.current;
      pendingStepRef.current = null;

      if (!shouldRequestRef.current) return;

      const effectiveValues = getEffectiveRequestValues();

      phaseRef.current = pending.nextPhase;
      pageRef.current = pending.nextPage;

      log(`[useGetAllPaginatedItemsV2][${runId}] RESUME from pending step`, { nextPhase: pending.nextPhase, nextPage: pending.nextPage, nextPageSize: pending.nextPageSize, effectiveChangedSince: effectiveChangedSinceRef.current, });

      setPage(pending.nextPage);
      setRequest(requestFunction(pending.nextPageSize, pending.nextPage, effectiveValues));
      kickFetch();
      return;
    }

    // 2) If we are mid-run and already have a request, do nothing (stay calm)
    if (phaseRef.current !== "DONE" && request) {
      log(`[useGetAllPaginatedItemsV2][${runId}] mid-run detected; keeping current request`, { phase: phaseRef.current, page: pageRef.current, });
      return;
    }

    // 3) Otherwise start a new run at page 1
    pageRef.current = 1;
    lastExposeTimeRef.current = null;

    if (incrementalEnabled) {
      runStartedAtRef.current = formatChangedSince(new Date());
      freshRowsRef.current = null;
    } else {
      runStartedAtRef.current = null;
      freshRowsRef.current = [];
    }

    setPage(1);

    const effectiveValues = getEffectiveRequestValues();

    log(`[useGetAllPaginatedItemsV2][${runId}] START new run`, { cacheKey, metaCacheSize, previewPageSize, pageSize, phase: metaCacheSize === 0 ? "PREVIEW" : "HEAD_FULL", runStartedAt: runStartedAtRef.current, storedChangedSince, effectiveChangedSince: effectiveChangedSinceRef.current, effectiveValues, incrementalEnabled, });

    if (metaCacheSize === 0) {
      phaseRef.current = "PREVIEW";
      setRequest(requestFunction(previewPageSize, 1, effectiveValues));
    } else {
      phaseRef.current = "HEAD_FULL";
      setRequest(requestFunction(pageSize, 1, effectiveValues));
    }

    kickFetch();
  }, [shouldRequest, isBoolRef(shouldRequest) ? shouldRequest.current : shouldRequest, isCacheLoaded, metaCacheSize, previewPageSize, pageSize, requestFunction, requestValues, request, refreshKey,]);

  // Process results ALWAYS (even while paused),
  // but ONLY schedule the next request if not paused.
  useEffect(() => {
    if (!results) return;

    const runId = getRunId();

    const incoming = (results as any).Items as ItemType[] | undefined;
    const remaining = safeParseInt((results as any).Remaining);
    const currentPhase = phaseRef.current;
    const pageSizeForPhase = currentPhase === "PREVIEW" ? previewPageSize : pageSize;

    log(`[useGetAllPaginatedItemsV2][${runId}] RESULTS received`, { phase: currentPhase, page: pageRef.current, pageState: page, incomingLen: Array.isArray(incoming) ? incoming.length : 0, remaining, cacheLength: cacheLengthRef.current, rowsLen: rowsRef.current.length, freshRowsLen: freshRowsRef.current?.length ?? null, effectiveChangedSince: effectiveChangedSinceRef.current, runStartedAt: runStartedAtRef.current, status, });

    // Empty page
    if (!incoming || incoming.length === 0) {
      const activeLen = incrementalEnabled ? rowsRef.current.length : freshRowsRef.current?.length ?? 0;
      const hitCap = TEMP_CAP_AT_10000 && activeLen >= CAP_LIMIT;

      const step = decideNextStep(currentPhase, hitCap, remaining, pageSizeForPhase, cacheLengthRef.current, pageRef.current, incrementalEnabled);

      log(`[useGetAllPaginatedItemsV2][${runId}] empty page handling`, { hitCap, decided: step, });

      if (step.kind === "continue") {
        const nextPhase = step.nextPhase;
        const nextPage = step.nextPage;
        const nextPageSize = nextPhase === "PREVIEW" ? previewPageSize : pageSize;

        if (pausedRef.current || !shouldRequestRef.current) {
          warn(`[useGetAllPaginatedItemsV2][${runId}] would continue but paused`, { paused: pausedRef.current, shouldRequest: shouldRequestRef.current, });
          pendingStepRef.current = null;
          return;
        }

        const effectiveValues = getEffectiveRequestValues();

        phaseRef.current = nextPhase;
        pageRef.current = nextPage;

        log(`[useGetAllPaginatedItemsV2][${runId}] scheduling NEXT request (empty-page path)`, { nextPhase, nextPage, nextPageSize, effectiveValues, });

        setPage(nextPage);
        setRequest(requestFunction(nextPageSize, nextPage, effectiveValues));
        kickFetch();
      } else {
        phaseRef.current = "DONE";

        const snapshot = incrementalEnabled ? rowsRef.current.slice() : (freshRowsRef.current ?? rowsRef.current).slice();
        rowsRef.current = snapshot;
        freshRowsRef.current = null;
        rebuildRowIndex(snapshot);
        setItems(snapshot);
        persistSnapshot(snapshot);

        log(`[useGetAllPaginatedItemsV2][${runId}] DONE reached (empty-page path), cache replaced`, { len: snapshot.length, incrementalEnabled, });

        if (incrementalEnabled && runStartedAtRef.current) {
          const nextMarker = computeNextMarker({ runStartedAt: runStartedAtRef.current });
          setChangedSince(incrementalKey, nextMarker);

          log(`[useGetAllPaginatedItemsV2][${runId}] changedSince stored`, { incrementalKey, nextMarker, });
        }

        restartIfRefreshPending();
      }
      return;
    }

    const isFirstPage = pageRef.current === 1;
    let updated: ItemType[];
    let displayChanged = false;

    if (!incrementalEnabled) {
      if (currentPhase === "PREVIEW") {
        updated = incoming.slice();

        // PREVIEW is only a temporary presentation when there is no cache.
        // It is not added to freshRowsRef unless the preview response is already complete.
        if (remaining === 0) {
          freshRowsRef.current = updated;
        }

        if (cacheLengthRef.current === 0) {
          rowsRef.current = updated;
          rebuildRowIndex(updated);
          setItems(updated.slice());
        }
      } else {
        if (!freshRowsRef.current) freshRowsRef.current = [];

        freshRowsRef.current.push(...incoming);
        updated = freshRowsRef.current;

        // Quick path: patch currently displayed cached rows while the full fresh result is still loading.
        // Missing cached rows are intentionally kept until DONE, where freshRowsRef becomes authoritative.
        if (cacheLengthRef.current > 0) {
          displayChanged = mergeIncomingIntoDisplayedRows(incoming, { prependNew: isFirstPage, });
        }

        // Quick path for the no-cache preview flow: as soon as the first real full page arrives,
        // overlay it onto the already-displayed preview rows so new/updated entries show immediately.
        // This intentionally does not remove anything; final cleanup still happens only at DONE.
        if (cacheLengthRef.current === 0 && isFirstPage) {
          displayChanged = mergeIncomingIntoDisplayedRows(incoming);
        }
      }
    } else {
      displayChanged = mergeIncomingIntoDisplayedRows(incoming);
      updated = rowsRef.current;

      if (displayChanged) {
        cacheDirtyRef.current = true;
      }
    }

    if (TEMP_CAP_AT_10000 && updated.length > CAP_LIMIT) {
      updated = updated.slice(0, CAP_LIMIT);

      if (incrementalEnabled) {
        rowsRef.current = updated;
        rebuildRowIndex(updated);
      } else if (currentPhase !== "PREVIEW" || remaining === 0) {
        freshRowsRef.current = updated;
      }
    }

    const activeLen = incrementalEnabled ? rowsRef.current.length : updated.length;
    const hitCap = TEMP_CAP_AT_10000 && activeLen >= CAP_LIMIT;
    const isTailFinished = remaining === 0 || hitCap;

    const now = Date.now();
    const timeSinceLastExpose = lastExposeTimeRef.current == null ? Infinity : now - lastExposeTimeRef.current;

    const shouldExposeNow = isFirstPage || isTailFinished || timeSinceLastExpose >= MIN_UPDATE_INTERVAL_MS;

    log(`[useGetAllPaginatedItemsV2][${runId}] merge complete`, { isFirstPage, updatedLen: updated.length, hitCap, isTailFinished, timeSinceLastExpose, shouldExposeNow, incrementalEnabled, currentPhase, displayChanged, });

    if (incrementalEnabled && shouldExposeNow && (displayChanged || isFirstPage || isTailFinished)) {
      lastExposeTimeRef.current = now;
      setItems(rowsRef.current.slice());
    }

    if (!incrementalEnabled && currentPhase === "HEAD_FULL" && cacheLengthRef.current > 0 && shouldExposeNow && displayChanged) {
      lastExposeTimeRef.current = now;
      setItems(rowsRef.current.slice());
    }

    if (!incrementalEnabled && currentPhase === "HEAD_FULL" && cacheLengthRef.current === 0 && isFirstPage && shouldExposeNow && displayChanged) {
      lastExposeTimeRef.current = now;
      setItems(rowsRef.current.slice());
    }

    if (!incrementalEnabled && currentPhase === "PREVIEW" && cacheLengthRef.current === 0 && shouldExposeNow) {
      lastExposeTimeRef.current = now;
      setItems(updated.slice());
    }

    const step = decideNextStep(currentPhase, hitCap, remaining, pageSizeForPhase, cacheLengthRef.current, pageRef.current, incrementalEnabled);

    log(`[useGetAllPaginatedItemsV2][${runId}] decideNextStep`, { decided: step, currentPhase, page: pageRef.current, remaining, hitCap, pageSizeForPhase, cacheLength: cacheLengthRef.current, incrementalEnabled, });

    if (step.kind === "continue") {
      const nextPhase = step.nextPhase;
      const nextPage = step.nextPage;
      const nextPageSize = nextPhase === "PREVIEW" ? previewPageSize : pageSize;

      if (pausedRef.current || !shouldRequestRef.current) {
        warn(`[useGetAllPaginatedItemsV2][${runId}] would continue but paused`, { paused: pausedRef.current, shouldRequest: shouldRequestRef.current, });
        pendingStepRef.current = null;
        return;
      }

      const effectiveValues = getEffectiveRequestValues();

      phaseRef.current = nextPhase;
      pageRef.current = nextPage;

      log(`[useGetAllPaginatedItemsV2][${runId}] scheduling NEXT request`, { nextPhase, nextPage, nextPageSize, effectiveValues, });

      setPage(nextPage);
      setRequest(requestFunction(nextPageSize, nextPage, effectiveValues));
      kickFetch();
      return;
    }

    phaseRef.current = "DONE";

    const snapshot = incrementalEnabled ? rowsRef.current.slice() : (freshRowsRef.current ?? updated).slice();
    rowsRef.current = snapshot;
    freshRowsRef.current = null;
    rebuildRowIndex(snapshot);
    setItems(snapshot);
    persistSnapshot(snapshot);

    log(`[useGetAllPaginatedItemsV2][${runId}] DONE reached, cache replaced`, { len: snapshot.length, incrementalEnabled, });

    if (incrementalEnabled && runStartedAtRef.current) {
      const nextMarker = computeNextMarker({ runStartedAt: runStartedAtRef.current });
      setChangedSince(incrementalKey, nextMarker);

      log(`[useGetAllPaginatedItemsV2][${runId}] changedSince stored`, { incrementalKey, nextMarker, });
    }

    restartIfRefreshPending();
  }, [results, requestFunction, requestValues, pageSize, previewPageSize, cacheKey, setCachedValue, incrementalEnabled,]);

  useEffect(() => {
    const runId = getRunId();
    return () => {
      const currentLen = rowsRef.current.length;
      log(`[useGetAllPaginatedItemsV2][${runId}] UNMOUNT cleanup`, { currentLen, cacheLength: cacheLengthRef.current, phase: phaseRef.current, page: pageRef.current, effectiveChangedSince: effectiveChangedSinceRef.current, cacheDirty: cacheDirtyRef.current, });
      if (cacheDirtyRef.current) {
        let snapshot = rowsRef.current.slice();
        if (TEMP_CAP_AT_10000 && snapshot.length > CAP_LIMIT) {
          snapshot = snapshot.slice(0, CAP_LIMIT);
        }
        setToDB(cacheKey, snapshot as any);
        cacheLengthRef.current = snapshot.length;
        writeCacheSizeMeta(cacheKey, cacheLengthRef.current);
        cacheDirtyRef.current = false;
        log(`[useGetAllPaginatedItemsV2][${runId}] UNMOUNT wrote snapshot to IDB`, { len: snapshot.length, cacheKey, });
      }
    };
  }, [cacheKey]);

  return { rows: items, status };
};

const TEMP_CAP_AT_10000 = true;

const CAP_LIMIT = 12999;

const ENABLE_DEBUG = false;

const MIN_UPDATE_INTERVAL_MS = 5000;

function decideNextStep(phase: PaginationPhase, hitCap: boolean, remaining: number, pageSize: number, cacheLength: number, currentPage: number, incrementalEnabled: boolean): NextStep {
  const cachedPages = pageSize > 0 ? Math.floor(cacheLength / pageSize) : 0;
  const isTailFinished = remaining === 0 || hitCap;
  switch (phase) {
    case "PREVIEW": {
      if (isTailFinished) return { kind: "done" };
      return { kind: "continue", nextPhase: "HEAD_FULL", nextPage: 1 };
    }
    case "HEAD_FULL": {
      if (isTailFinished) return { kind: "done" };
      if (!incrementalEnabled) {
        return { kind: "continue", nextPhase: "HEAD_FULL", nextPage: currentPage + 1 };
      }
      const startTailPage = Math.max(2, cachedPages + 1);
      return { kind: "continue", nextPhase: "TAIL", nextPage: startTailPage };
    }
    case "TAIL": {
      if (!isTailFinished) {
        return { kind: "continue", nextPhase: "TAIL", nextPage: currentPage + 1 };
      }
      return { kind: "done" };
    }
    case "DONE":
    default:
      return { kind: "done" };
  }
}

const CACHE_META_PREFIX = "cache_size_";

function readCacheSizeMeta(cacheKey: string): number {
  if (typeof window === "undefined") return 0;
  try {
    const raw = window.localStorage.getItem(CACHE_META_PREFIX + cacheKey);
    if (!raw) return 0;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  } catch {
    return 0;
  }
}

function writeCacheSizeMeta(cacheKey: string, size: number) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CACHE_META_PREFIX + cacheKey, String(size >= 0 ? size : 0));
  } catch {
    // ignore
  }
}

const isBoolRef = (v: any): v is BoolRef => !!v && typeof v === "object" && "current" in v;

const PAD2 = (n: number) => String(n).padStart(2, "0");

// changedSince format: YYYY-MM-DD HH:MM:SS
function formatChangedSince(dt: Date): string {
  const y = dt.getFullYear();
  const m = PAD2(dt.getMonth() + 1);
  const d = PAD2(dt.getDate());
  const hh = PAD2(dt.getHours());
  const mm = PAD2(dt.getMinutes());
  const ss = PAD2(dt.getSeconds());
  return `${y}-${m}-${d} ${hh}:${mm}:${ss}`;
}

function safeParseInt(v: any): number {
  const n = typeof v === "number" ? v : parseInt(String(v ?? ""), 10);
  return Number.isFinite(n) ? n : 0;
}

function usePaginatedDebugLogger(enabled: boolean) {
  const runIdRef = useRef<string | null>(null);
  const getRunId = () => {
    if (!runIdRef.current) runIdRef.current = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    return runIdRef.current;
  };
  const log = (...args: any[]) => {
    if (!enabled) return;
    console.log(...args);
  };
  const warn = (...args: any[]) => {
    if (!enabled) return;
    console.log(...args);
  };
  return { getRunId, log, warn };
}

type IdPath = string | string[];

type UseGetAllPaginatedItemsResult<ItemType> = {
  rows: ItemType[] | null;
  status: string;
};

type PaginationPhase = "PREVIEW" | "HEAD_FULL" | "TAIL" | "DONE";

type NextStep =
  | { kind: "continue"; nextPhase: PaginationPhase; nextPage: number }
  | { kind: "done" };

type PendingStep = {
  nextPhase: PaginationPhase;
  nextPage: number;
  nextPageSize: number;
} | null;

type BoolRef = { current: boolean };

type BoolOrRef = boolean | BoolRef;

type IncrementalConfig = {
  enabled: boolean;
  key?: string;
  requireBaseCache?: boolean;
  buildRequestValues?: (baseValues: any, changedSince: string | null) => any;
  computeNextMarker?: (ctx: { runStartedAt: string }) => string;
};

type UseGetAllPaginatedItemsConfig<ItemType> = {
  requestFunction: (pageSize: number, page: number, values?: any) => AxiosRequestConfig;
  pageSize?: number;
  idField?: IdPath;
  requestValues?: any;
  shouldRequest?: BoolOrRef;
  incremental?: IncrementalConfig | null;
  refreshKey?: unknown;
};

export interface PaginationResult<T> {
  PageSize?: number;
  Page?: number;
  FirstId?: number;
  LastId?: number;
  TotalCount?: number;
  Remaining?: number;
  Items: T[];
}
